import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Card, CardContent, Grid, CircularProgress, List, ListItem, 
    ListItemButton, ListItemText, Button, Modal, Divider, Chip, Paper, IconButton, 
    Link as MuiLink, CardActionArea, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const navigate = useNavigate();

  const style = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: '90%', maxWidth: 500, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4,
    maxHeight: '90vh', overflowY: 'auto'
  };

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      axios.get('/api/admin/stats'),
      axios.get('/api/admin/pending-users'),
      axios.get('/api/admin/pending-businesses')
    ]).then(([statsRes, usersRes, businessesRes]) => {
      setStats(statsRes.data);
      setPendingUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setPendingBusinesses(Array.isArray(businessesRes.data) ? businessesRes.data : []);
    }).catch(err => console.error(err)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleViewUserDetails = (user) => setSelectedUser(user);
  const handleViewBusinessDetails = (business) => setSelectedBusiness(business);
  const handleApproval = (type, id, action) => {
    axios.put(`/api/admin/${action}-${type}/${id}`).then(() => {
      setSelectedUser(null);
      setSelectedBusiness(null);
      fetchData();
    });
  };

  if (loading || !stats) return <CircularProgress />;

  return (
    <Box>
       <Box sx={{ 
         p: { xs: 2, sm: 3, md: 4 }, 
         backgroundColor: 'primary.main', 
         color: 'white', 
         borderRadius: 2, 
         mb: { xs: 2, md: 4 } 
       }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Bentornato/a!
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Gestisci la rete alumni e rivedi le approvazioni in sospeso.
        </Typography>
      </Box>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Utenti in Attesa</Typography>
              <Typography variant="h4">{stats.pendingUsers}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Aziende in Attesa</Typography>
              <Typography variant="h4">{stats.pendingBusinesses}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Totale Alumni</Typography>
              <Typography variant="h4">{stats.totalAlumni}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Aziende Partner</Typography>
              <Typography variant="h4">{stats.totalBusinesses}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Approvazioni Utenti
          </Typography>
          <Card>
            {Array.isArray(pendingUsers) && pendingUsers.length > 0 ? (
              <List>
                {pendingUsers.map(user => (
                  <ListItemButton key={user.id} onClick={() => handleViewUserDetails(user)}>
                    <ListItemText 
                      primary={user.fullName} 
                      secondary={user.email}
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography sx={{ p: 2 }}>Nessun utente in attesa.</Typography>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Approvazioni Aziende
          </Typography>
          <Card>
            {Array.isArray(pendingBusinesses) && pendingBusinesses.length > 0 ? (
              <List>
                {pendingBusinesses.map(biz => (
                  <ListItemButton key={biz.id} onClick={() => handleViewBusinessDetails(biz)}>
                    <ListItemText 
                      primary={biz.name} 
                      secondary={biz.email}
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography sx={{ p: 2 }}>Nessuna azienda in attesa.</Typography>
            )}
          </Card>
        </Grid>
      </Grid>
      <Modal open={!!selectedUser} onClose={() => setSelectedUser(null)}>
        <Box sx={style}>
          {selectedUser && (
            <>
              <Typography variant="h6">Dettagli Utente in Approvazione</Typography>
              <Typography sx={{ mt: 2 }}><b>Nome:</b> {selectedUser.fullName}</Typography>
              <Typography><b>Email:</b> {selectedUser.email}</Typography>
              <Typography><b>Telefono:</b> {selectedUser.phone || 'N/D'}</Typography>
              <Typography><b>Nato/a il:</b> {new Date(selectedUser.birthDate).toLocaleDateString('it-IT')}</Typography>
              <Typography><b>Competenze:</b> {selectedUser.categories.map(c => c.category.name).join(', ')}</Typography>
              <Typography>
                <b>CV:</b> {selectedUser.resume ? <MuiLink href={`/${selectedUser.resume}`} target="_blank" rel="noopener noreferrer">Scarica</MuiLink> : 'Non caricato'}
              </Typography>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="outlined" color="error" onClick={() => handleApproval('user', selectedUser.id, 'reject')}>Rifiuta</Button>
                <Button variant="contained" color="success" onClick={() => handleApproval('user', selectedUser.id, 'approve')}>Approva</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      <Modal open={!!selectedBusiness} onClose={() => setSelectedBusiness(null)}>
         <Box sx={style}>
          {selectedBusiness && (
            <>
              <Typography variant="h6">Dettagli Azienda in Approvazione</Typography>
              <Typography sx={{ mt: 2 }}><b>Azienda:</b> {selectedBusiness.name}</Typography>
              <Typography><b>Email:</b> {selectedBusiness.email}</Typography>
              <Typography><b>Referente:</b> {selectedBusiness.contactPerson}</Typography>
              <Typography><b>Settore:</b> {selectedBusiness.sector}</Typography>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="outlined" color="error" onClick={() => handleApproval('business', selectedBusiness.id, 'reject')}>Rifiuta</Button>
                <Button variant="contained" color="success" onClick={() => handleApproval('business', selectedBusiness.id, 'approve')}>Approva</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};
  
const UserDashboard = ({ user }) => {
    const [stats, setStats] = useState(null);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
  
    const fetchData = () => {
      setLoading(true);
      Promise.all([
        axios.get('/api/stats/public'),
        axios.get('/api/users/my-applications')
      ]).then(([statsRes, appsRes]) => {
        setStats(statsRes.data);
        setApplications(appsRes.data);
      }).catch(err => console.error("Errore caricamento dati dashboard utente:", err))
        .finally(() => setLoading(false));
    };
  
    useEffect(() => { fetchData(); }, []);
  
    const handleWithdraw = (applicationId) => {
      if (window.confirm('Sei sicuro di voler ritirare questa candidatura?')) {
        axios.delete(`/api/applications/${applicationId}`)
          .then(() => fetchData())
          .catch(err => alert('Errore: impossibile ritirare la candidatura.'));
      }
    };
  
    if (loading) return <CircularProgress />;
  
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Bentornato/a, {user.name}!</Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6}><Card><CardContent><Typography>Aziende Partner</Typography><Typography variant="h4">{stats?.totalBusinesses}</Typography></CardContent></Card></Grid>
          <Grid item xs={12} sm={6}><Card><CardContent><Typography>Nuove Offerte (ultimi 7gg)</Typography><Typography variant="h4">{stats?.newJobsCount}</Typography></CardContent></Card></Grid>
        </Grid>
        <Typography variant="h5" gutterBottom>Le Tue Candidature Recenti</Typography>
        <Paper>
          <List>
            {applications.length > 0 ? applications.map((app, index) => (
              <React.Fragment key={app.id}>
                <ListItem secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleWithdraw(app.id)} title="Ritira candidatura">
                      <DeleteIcon color="error" />
                    </IconButton>
                  }>
                  <ListItemButton onClick={() => navigate(`/opportunita/${app.job.id}`)}>
                    <ListItemText 
                      primary={app.job.title} 
                      secondary={`Presso ${app.job.business.name} - Inviata il ${new Date(app.createdAt).toLocaleDateString()}`} 
                    />
                    <Chip label={app.status} size="small" />
                  </ListItemButton>
                </ListItem>
                {index < applications.length - 1 && <Divider />}
              </React.Fragment>
            )) : <Typography sx={{ p: 2 }}>Non ti sei ancora candidato per nessuna offerta.</Typography>}
          </List>
        </Paper>
      </Box>
    );
};
  
const BusinessDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    axios.get('/api/stats/business')
      .then(statsRes => {
        setStats(statsRes.data);
      })
      .catch(err => console.error("Errore caricamento dati azienda:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Bentornato/a, {user.name}!</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography>Alumni sulla Piattaforma</Typography>
              <Typography variant="h4">{stats?.totalAlumni}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography>I Tuoi Annunci Attivi</Typography>
              <Typography variant="h4">{stats?.activeJobs}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Gestisci le tue opportunità</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/azienda/opportunita')}
        >
          Visualizza Opportunità
        </Button>
      </Box>

      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Accedi alla sezione Opportunità per gestire e pubblicare nuovi annunci di lavoro.
        </Typography>
      </Paper>
    </Box>
  );
};
  
const Dashboard = () => {
    const { user } = useAuth();
    if (!user) return <CircularProgress />;
  
    if (user.role === 'admin') {
      return <AdminDashboard />;
    }
    
    if (user.role === 'user') {
      return <UserDashboard user={user} />;
    }
  
    if (user.role === 'business') {
      return <BusinessDashboard user={user} />;
    }
  
    return <Typography>Ruolo utente non riconosciuto.</Typography>;
};

export default Dashboard;